import type { SoundDefinition } from "./SoundDefinition.js";
import type {
  SoundPlayer,
  SoundPlayHandle,
  SoundPlayOptions,
} from "./SoundPlayer.js";

/**
 * mp3 부재 fallback — WebAudio API로 짧은 sine 톤을 동적 생성.
 *
 * 사운드 id를 해시 → frequency(220~880Hz)·duration(80~380ms) 매핑.
 * 같은 id는 항상 같은 톤이 나오므로 사용자가 "이 톤이 X 액션의 신호"로 학습 가능.
 *
 * BGM(loop=true)은 두 음(기본 + 1.2배 주파수)을 0.45s 간격으로 무한 반복 — 짧은 멜로디 시뮬.
 *
 * 한계:
 * - 실시간 `setVolume`은 무시 (이미 schedule된 envelope에 영향 없음). 다음 `play`부터 반영.
 * - AudioContext autoplay 정책: 사용자 제스처 전 `context.state === "suspended"`면 silent skip.
 *   `unlock()` 호출 후 다시 `play`로 정상 재생.
 */
export class SineTonePlayer implements SoundPlayer {
  private context: AudioContext | null = null;
  private readonly active = new Map<symbol, () => void>();

  async load(_definition: SoundDefinition): Promise<void> {
    return;
  }

  play(definition: SoundDefinition, options: SoundPlayOptions): SoundPlayHandle {
    const key = Symbol(definition.id);
    const handle: SoundPlayHandle = { id: definition.id, key };

    let ctx: AudioContext;
    try {
      ctx = this.ensureContext();
    } catch (err) {
      console.warn(
        `[SineTonePlayer] context unavailable for ${definition.id}: ${
          (err as Error).message
        }`,
      );
      return handle;
    }

    if (ctx.state === "suspended") {
      // 사용자 제스처 전, 또는 unlock 타이밍 어긋남/백그라운드 복귀로 재-suspend된 상태.
      // resume()을 1회 lazy 시도해 영구 무음을 방지한다(이번 재생은 비동기 resume이라 skip,
      // 다음 play부터 자연 복구). resume 거부는 무시.
      void ctx.resume().catch(() => {});
      return handle;
    }

    const gain = ctx.createGain();
    gain.gain.value = options.volume;
    gain.connect(ctx.destination);

    if (options.loop) {
      const stopFn = this.playLoop(ctx, gain, definition.id);
      this.active.set(key, () => {
        stopFn();
        gain.disconnect();
      });
    } else {
      // toneHz가 정의돼 있으면 음정 직접 재생(음계 비프 등), 없으면 id 해시로 고유 톤.
      const { freq, duration } =
        definition.toneHz !== undefined
          ? { freq: definition.toneHz, duration: (definition.toneMs ?? 120) / 1000 }
          : sfxParams(definition.id);
      const osc = ctx.createOscillator();
      osc.type = "sine";
      // pitchMultiplier(콤보 연타 등 동적 음정) — 미지정 시 1(원음).
      osc.frequency.value = freq * (options.pitchMultiplier ?? 1);
      const now = ctx.currentTime;
      // 짧은 attack + linear decay 으로 클릭 노이즈 회피
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(options.volume, now + 0.01);
      gain.gain.linearRampToValueAtTime(0, now + duration);
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + duration);
      let cleaned = false;
      const cleanup = (callOnEnded: boolean): void => {
        if (cleaned) return;
        cleaned = true;
        osc.disconnect();
        gain.disconnect();
        this.active.delete(key);
        if (callOnEnded && options.onEnded !== undefined) {
          options.onEnded();
        }
      };
      osc.onended = (): void => cleanup(true);
      this.active.set(key, () => {
        try {
          osc.stop();
        } catch {
          /* already stopped */
        }
        cleanup(false);
      });
    }

    return handle;
  }

  private playLoop(ctx: AudioContext, gain: GainNode, id: string): () => void {
    const { freq, duration } = bgmParams(id);
    const notes = [freq, freq * 1.2];
    let stopped = false;
    let noteIdx = 0;
    let currentOsc: OscillatorNode | null = null;

    const scheduleNext = (): void => {
      if (stopped) return;
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = notes[noteIdx % notes.length]!;
      noteIdx += 1;
      osc.connect(gain);
      const now = ctx.currentTime;
      osc.start(now);
      osc.stop(now + duration);
      osc.onended = (): void => {
        osc.disconnect();
        if (!stopped) scheduleNext();
      };
      currentOsc = osc;
    };

    scheduleNext();
    return () => {
      stopped = true;
      if (currentOsc !== null) {
        try {
          currentOsc.stop();
        } catch {
          /* already stopped */
        }
      }
    };
  }

  stop(handle: SoundPlayHandle): void {
    const stopFn = this.active.get(handle.key);
    if (stopFn === undefined) return;
    stopFn();
    this.active.delete(handle.key);
  }

  setVolume(_handle: SoundPlayHandle, _volume: number): void {
    // sine 톤은 envelope 단위 — 실시간 볼륨 변경 무시. 다음 play부터 반영.
  }

  stopAll(): void {
    for (const stopFn of this.active.values()) {
      stopFn();
    }
    this.active.clear();
  }

  async unlock(): Promise<void> {
    const ctx = this.ensureContext();
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
  }

  dispose(): void {
    this.stopAll();
    if (this.context !== null) {
      void this.context.close();
      this.context = null;
    }
  }

  private ensureContext(): AudioContext {
    if (this.context !== null) return this.context;
    type WebkitGlobal = { webkitAudioContext?: typeof AudioContext };
    const globalCtor =
      typeof AudioContext !== "undefined"
        ? AudioContext
        : (typeof window !== "undefined"
            ? (window as unknown as WebkitGlobal).webkitAudioContext
            : undefined);
    if (globalCtor === undefined) {
      throw new Error("[SineTonePlayer] AudioContext not supported");
    }
    this.context = new globalCtor();
    return this.context;
  }
}

function sfxParams(id: string): { freq: number; duration: number } {
  const hash = hashString(id);
  const freq = 220 + (hash % 660);
  const duration = 0.08 + ((hash >>> 8) % 300) / 1000;
  return { freq, duration };
}

function bgmParams(id: string): { freq: number; duration: number } {
  const hash = hashString(id);
  const freq = 180 + (hash % 200);
  return { freq, duration: 0.45 };
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}
